import {
  INTAKE_BUSY_MESSAGE,
  INTAKE_EXPIRED_MESSAGE,
  INTAKE_RATE_LIMITED_MESSAGE,
  type IntakeSession,
  type IntakeStatusUpdate,
} from '../intake-client';
import { EMAIL_DELETE_PENDING_MESSAGE, EMAIL_DELETED_MESSAGE } from './copy';
import type { Toast } from './toast';

/** What load() found among the addresses this browser remembers. */
export interface StoredIntake {
  /** The live address this page may resume (this tab's own one first, else the newest), or null. */
  readonly session: IntakeSession | null;
  /** When nothing is resumable: the newest live address another open page took over from this one. */
  readonly elsewhere: IntakeSession | null;
}

/**
 * What the e-mail card and the app need from the intake client. The browser
 * adapter (intake-adapter.ts) wraps ../intake-client; previews and tests pass
 * fakes. Deadlines are on this device's clock (see IntakeSession).
 */
export interface IntakeApi {
  /** A fresh address, already held by this page. Never waits for earlier deletes. */
  create(signal?: AbortSignal): Promise<IntakeSession>;
  status(session: IntakeSession, signal?: AbortSignal): Promise<IntakeStatusUpdate>;
  download(session: IntakeSession, status: IntakeStatusUpdate, signal?: AbortSignal): Promise<File[]>;
  /**
   * Stops using the address in this page and deletes it, with anything it
   * received, on the server. Resolves true once the server confirmed it, false
   * when the delete did not get through; it is then sent again when the
   * connection is back, the page shows again or the tool is opened again, and
   * the address is never resumed meanwhile. Never rejects.
   */
  discard(session: IntakeSession): Promise<boolean>;
  /** Remembers the address so a reopened page can resume it. False when this browser could not store it. */
  save(session: IntakeSession): boolean;
  load(): StoredIntake;
  /** Holds the address in this page, taking it over from any other open page. */
  take(session: IntakeSession): Promise<void>;
  /** Called with the address id when another open page takes over one this page holds. Returns an unsubscribe function. */
  onLost(listener: (sessionId: string) => void): () => void;
  /**
   * Forgets an address that is gone anyway (expired, or unknown to the server)
   * without deleting anything on the server: its own resume entry (never
   * another tab's) and this page's hold.
   */
  clear(session: IntakeSession): void;
}

/**
 * How long discardIntakeAddress waits for the server to confirm the delete.
 * On a connection that drops requests silently, discard() only gives up when
 * the request times out (15 s), long after the card went back to idle.
 */
export const DISCARD_CONFIRM_WAIT_MS = 1_500;

/**
 * '그만두고 주소 지우기' (card) and '주소 지우고 처음으로' (error screen): deletes
 * the address and shows exactly one toast, within DISCARD_CONFIRM_WAIT_MS. It
 * says the address is gone only when the server confirmed that in time;
 * otherwise (the delete did not get through, or no answer yet) it says when it
 * will be, and a confirmation that comes later shows nothing more. The adapter
 * keeps sending the delete until the server confirms it. discard() runs as
 * soon as this is called, so a start screen mounted right after never resumes
 * the address. Resolves once the toast is shown: true when it said the address
 * is gone.
 */
export function discardIntakeAddress(api: Pick<IntakeApi, 'discard'>, session: IntakeSession, toast: Toast): Promise<boolean> {
  let discarded: Promise<boolean>;
  try {
    discarded = Promise.resolve(api.discard(session));
  } catch {
    discarded = Promise.resolve(false);
  }
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let told = false;
    const tell = (deleted: boolean): void => {
      if (told) return;
      told = true;
      clearTimeout(timer);
      toast(deleted ? EMAIL_DELETED_MESSAGE : EMAIL_DELETE_PENDING_MESSAGE);
      resolve(deleted);
    };
    timer = setTimeout(() => tell(false), DISCARD_CONFIRM_WAIT_MS);
    discarded.then((deleted) => tell(deleted === true), () => tell(false));
  });
}

export type IntakeErrorKind = 'aborted' | 'rate-limited' | 'busy' | 'gone' | 'network' | 'server';

function numberField(value: object, key: string): number | null {
  const field = (value as Record<string, unknown>)[key];
  return typeof field === 'number' ? field : null;
}

function stringField(value: object, key: string): string | null {
  const field = (value as Record<string, unknown>)[key];
  return typeof field === 'string' ? field : null;
}

/**
 * Classifies IntakeClientError (code/status from ../intake-client), fetch
 * failures and aborts into what the card needs to decide.
 */
export function classifyIntakeError(error: unknown): IntakeErrorKind {
  if (!error || typeof error !== 'object') return 'server';
  const name = stringField(error, 'name');
  if (name === 'AbortError') return 'aborted';
  const status = numberField(error, 'status');
  const code = (stringField(error, 'code') ?? '').toUpperCase();
  if (code === 'BUSY' || code === 'INTAKE_BUSY') return 'busy';
  if (status === 429 || code === 'TRY_AGAIN_LATER' || code === 'RATE_LIMITED') return 'rate-limited';
  if (status === 404 || status === 410 || code === 'NOT_FOUND') return 'gone';
  if (code === 'NETWORK' || code === 'TIMEOUT' || code === 'OFFLINE' || name === 'TypeError') return 'network';
  return 'server';
}

/** Copy for each kind. None of it blames the person: many phones share one IP address. */
export function intakeErrorMessage(kind: IntakeErrorKind): string {
  switch (kind) {
    case 'rate-limited':
      return INTAKE_RATE_LIMITED_MESSAGE;
    case 'busy':
      return INTAKE_BUSY_MESSAGE;
    case 'gone':
      return INTAKE_EXPIRED_MESSAGE;
    case 'network':
      return '인터넷 연결을 확인하고 다시 시도해 주세요.';
    case 'aborted':
      return '요청을 멈췄어요.';
    case 'server':
      return '메일 받기를 잠시 쓸 수 없어요. 파일로 올리거나 잠시 후 다시 시도해 주세요.';
  }
}
